import { useEffect, useRef } from 'react'
import { enum__ComponentType } from '../../common/Dictionaries';
import { isNullUndefined } from '../../common/Helpers';
import { __OBJECT_STRUCTURE_BoardFilters } from '../../common/ObjectStructures/sKPI';
import { getBoardComponentInfoRecursiveValuesPortfolio } from '../../services/sKPI/board.service';
import { useDispatch } from 'react-redux';
import {
    action_setCompareContracts_ChartData,
    action_setCompareContracts_ChartDataBase,
    action_setCompareContracts_ChartData_Structure,
    action_setCompareContracts_FireGetData,
    action_setCompareContracts_OPFData
} from '../../reducers/actions/KPI/compareContractsActions';
import i18next from 'i18next';


const useSearch = (
    defaultSelectedFiltersBinded,
    setDefaultSelectedFiltersBinded,
    contextSite,
    selectedContracts,
    inputParam_BoardId,
    pilarSeleccionado,
    segmentSelected
) => {
    const dispatch = useDispatch();

    const abortControllerRef = useRef(null);
    const lastRequestIdRef = useRef(0);
    const inFlightRequestKeyRef = useRef(null);

    const getSafeJson = (value) => {
        try {
            return JSON.stringify(value);
        }
        catch {
            return String(value);
        }
    };

    useEffect(() => {
        const doIt = async () => {
            const calendarConfig = contextSite.kpiModule.boardCalendarFilterConfig.value;

            const requestKey = JSON.stringify({
                boardId: inputParam_BoardId,
                componentTypeId: enum__ComponentType.CONSOLIDATE_GROUP_CONSOLIDATE_CHART,
                selectedContracts: selectedContracts
                    .map(x => x.elementId)
                    .sort((a, b) => a - b),
                pilarScopeId: pilarSeleccionado?.scopeId ?? null,
                segmentSelected: getSafeJson(segmentSelected),
                calendarSelectorId: calendarConfig.calendarSelectorId,
                periodStartDate: calendarConfig.periodStartDate,
                periodEndsDate: calendarConfig.periodEndsDate,
                displayAgregationId: calendarConfig.displayAgregationId
            });

            // Si la misma request ya está en curso, no hago nada.
            // Esto evita que una segunda ejecución del mismo useEffect cancele la primera.
            if (
                abortControllerRef.current &&
                !abortControllerRef.current.signal.aborted &&
                inFlightRequestKeyRef.current === requestKey
            ) {
                return;
            }

            // Si hay una request en curso, pero es distinta, ahí sí cancelo la anterior.
            if (
                abortControllerRef.current &&
                !abortControllerRef.current.signal.aborted &&
                inFlightRequestKeyRef.current !== requestKey
            ) {
                abortControllerRef.current.abort();
            }

            const controller = new AbortController();

            abortControllerRef.current = controller;
            inFlightRequestKeyRef.current = requestKey;

            const requestId = ++lastRequestIdRef.current;

            try {
                setDefaultSelectedFiltersBinded(true);

                let filter = __OBJECT_STRUCTURE_BoardFilters("element");
                filter.l_codes = selectedContracts.map(x => x.elementId.toString());

                let serviceResponse = await getBoardComponentInfoRecursiveValuesPortfolio(
                    filter,
                    inputParam_BoardId,
                    enum__ComponentType.CONSOLIDATE_GROUP_CONSOLIDATE_CHART,
                    null,
                    null,
                    calendarConfig.calendarSelectorId,
                    calendarConfig.periodStartDate,
                    calendarConfig.periodEndsDate,
                    calendarConfig.displayAgregationId,
                    controller.signal
                );

                // Si esta respuesta ya no corresponde a la última request válida, se ignora.
                if (requestId !== lastRequestIdRef.current) return;

                if (serviceResponse.data.succeeded) {
                    const allDataToCompare = serviceResponse.data.data[0].items;
                    const opfPilarData = serviceResponse.data.data[0].items.find(x => x.name === 'OPF');

                    let contractSeries = [];
                    let contractCategories = [];

                    if (opfPilarData.l_vals.length > 0) {
                        contractCategories = opfPilarData.l_vals.map(x => x.periodId.toString()).filter((value, index, self) => self.indexOf(value) === index);
                    }

                    selectedContracts.forEach(selectedContract => {
                        let contractSerie = {};
                        contractSerie.name = `${selectedContract.contractCode} - ${selectedContract.supplierName}`;
                        contractSerie.data = [];
                        contractSerie.isDashed = 0;

                        allDataToCompare.forEach(unmanipulatedDataItemWithMergedSeries => {
                            if (pilarSeleccionado.scopeId === unmanipulatedDataItemWithMergedSeries.id) {
                                contractCategories.forEach(category => {
                                    const aux = unmanipulatedDataItemWithMergedSeries.l_vals.find(
                                        x =>
                                            x.periodId.toString() === category &&
                                            x.elementId === selectedContract.elementId
                                    );

                                    contractSerie.data.push(
                                        !isNullUndefined(aux) && !isNullUndefined(aux.scopeValue)
                                            ? parseFloat(aux.scopeValue.toFixed(2)).toLocaleString(i18next.language)
                                            : null
                                    );
                                });
                            }
                        });

                        contractSeries.push(contractSerie);
                    });

                    if (pilarSeleccionado.scopeId === 0) {
                        let contractSerieOpf = {};
                        contractSerieOpf.name = `~ OPF ~`;
                        contractSerieOpf.data = [];
                        contractSerieOpf.isDashed = 5;

                        allDataToCompare
                            .filter(x => x.name === 'OPF')
                            .forEach(unmanipulatedDataItemWithMergedSeries => {
                                unmanipulatedDataItemWithMergedSeries.l_vals.forEach(unmanipulatedLValItem => {
                                    if (unmanipulatedLValItem.elementId === 0) {
                                        contractSerieOpf.data.push(
                                            !isNullUndefined(unmanipulatedLValItem.scopeValue)
                                                ? parseFloat(unmanipulatedLValItem.scopeValue.toFixed(2)).toLocaleString(i18next.language)
                                                : unmanipulatedLValItem.scopeValue
                                        );
                                    }
                                });
                            });

                        contractSeries.push(contractSerieOpf);
                    }
                    else {
                        let contractSerieOpf = {};
                        contractSerieOpf.name = `~ ${pilarSeleccionado.name} ~`;
                        contractSerieOpf.data = [];
                        contractSerieOpf.isDashed = 5;

                        const valoresPilar = allDataToCompare
                            .filter(x => x.name === pilarSeleccionado.name)
                            .flatMap(x => x.l_vals || []);

                        const valoresPorPeriodo = valoresPilar.reduce((acc, item) => {
                            const periodKey = item.periodId?.toString();

                            if (isNullUndefined(periodKey) || isNullUndefined(item.scopeValue)) {
                                return acc;
                            }

                            if (!acc[periodKey]) {
                                acc[periodKey] = [];
                            }

                            acc[periodKey].push(Number(item.scopeValue));

                            return acc;
                        }, {});

                        contractSerieOpf.data = contractCategories.map(category => {
                            const periodKey = category.toString();

                            const valoresPeriodo = valoresPorPeriodo[periodKey] || [];

                            if (valoresPeriodo.length === 0) {
                                return null;
                            }

                            const promedio =
                                valoresPeriodo.reduce((acc, value) => acc + value, 0) / valoresPeriodo.length;

                            return parseFloat(promedio.toFixed(2));
                        });

                        contractSeries.push(contractSerieOpf);
                    }

                    dispatch(action_setCompareContracts_ChartData_Structure(serviceResponse.data.data[0].items));

                    dispatch(action_setCompareContracts_ChartData({
                        chartId: 'CHART_PORTFOLIO',
                        contractSeries: contractSeries,
                        contractCategories: contractCategories
                    }));

                    dispatch(action_setCompareContracts_ChartDataBase({
                        chartId: 'CHART_PORTFOLIO',
                        contractSeries: contractSeries,
                        contractCategories: contractCategories
                    }));

                    dispatch(action_setCompareContracts_OPFData(opfPilarData));
                    dispatch(action_setCompareContracts_FireGetData(false));
                }
                else {
                    dispatch(action_setCompareContracts_ChartData(null));
                    dispatch(action_setCompareContracts_ChartDataBase(null));
                    dispatch(action_setCompareContracts_OPFData(null));
                    dispatch(action_setCompareContracts_FireGetData(false));
                }
            }
            catch (error) {
                if (
                    error.name === "CanceledError" ||
                    error.code === "ERR_CANCELED"
                ) {
                    return;
                }

                if (requestId !== lastRequestIdRef.current) return;

                dispatch(action_setCompareContracts_ChartData(null));
                dispatch(action_setCompareContracts_ChartDataBase(null));
                dispatch(action_setCompareContracts_OPFData(null));
                dispatch(action_setCompareContracts_FireGetData(false));
            }
            finally {
                if (abortControllerRef.current === controller) {
                    abortControllerRef.current = null;
                    inFlightRequestKeyRef.current = null;
                }
            }
        };

        if (defaultSelectedFiltersBinded === false) {
            doIt();
        }

    }, [
        defaultSelectedFiltersBinded,
        setDefaultSelectedFiltersBinded,
        selectedContracts,
        inputParam_BoardId,
        pilarSeleccionado,
        segmentSelected
    ]);
}

export {
    useSearch
}